import { useCallback, useEffect, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import api from "../api"
import { ReactComponent as ArchiveIcon } from "../assets/images/icons/ic-archive.svg"
import { ReactComponent as CopyIcon } from "../assets/images/icons/ic-copy.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg"
import { CourseCategories, CourseStatuses, CourseType, CourseTypes } from "../common/constants"
import StatusBadge from "../components/StatusBadge"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Select from "../components/Select"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import styles from "./Courses.module.css"

const ARCHIVED_CATEGORY_VALUE = '__archived__'

const Courses = () => {

  const { t } = useTranslation()
  const [courses, setCourses] = useState(null)
  const [filterCourses, setFilterCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState({ open: false, title: '', text: '' })
  const [selected, setSelected] = useState(0);
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filters, setFilters] = useState({ type: null, category: null, status: null })
  const categoryOptions = [...CourseCategories, { id: 999, name: 'Archiviati', value: ARCHIVED_CATEGORY_VALUE }]

  // Ordinamento personalizzato: prima draft, poi public, poi hidden; all'interno ordine alfabetico per nome
  function sortCourses(list) {
    const statusOrder = { draft: 0, public: 1, hidden: 2 }
    return [...list].sort((a, b) => {
      const sa = Object.prototype.hasOwnProperty.call(statusOrder, a.status) ? statusOrder[a.status] : 999
      const sb = Object.prototype.hasOwnProperty.call(statusOrder, b.status) ? statusOrder[b.status] : 999
      if (sa !== sb) return sa - sb
      return a.name.localeCompare(b.name)
    })
  }

  const getCourses = async () => {
    setLoading(true)
    try {
      let courses = await api.get("/admin/courses?complete=false")
      const sorted = sortCourses(courses)
      setCourses(sorted)
      setFilterCourses(sorted.filter(c => c.archived !== true))
    }
    catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!courses) {
      getCourses()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const duplicate = async (courseId, duplicateAll = false) => {
    try {
      let course = await api.get(`/admin/courses/${courseId}/duplicate?duplicate_all=${duplicateAll}`)
      const updated = sortCourses([...courses, course])
      setCourses(updated)
      setSelected(0)
      setFilterCourses(updated)
    }
    catch (e) {
      console.error(e)
    }
  }

  const deleteCourse = async (courseId) => {
    try {
      await api.delete(`/admin/courses/${courseId}`)
      setCourses(courses.filter(c => c.id !== courseId))
      setFilterCourses(filterCourses.filter(c => c.id !== courseId))
    }
    catch (e) {
      console.error(e)
    }
  }

  const setCourseArchived = async (courseId, archived) => {
    try {
      await api.put(`/admin/courses/${courseId}/archive?archived=${archived}`)
      setCourses((prev) => (prev || []).map((course) => {
        if (course.id !== courseId) return course
        return { ...course, archived }
      }))
    }
    catch (e) {
      console.error(e)
    }
  }

  const applyFilters = useCallback(() => {
    if (!courses) return
    if (q.trim().length === 0) {
      let base = courses
      switch (selected) {
        case 1:
          base = courses.filter(c => c.type === CourseType.Masterclass)
          break
        case 2:
          base = courses.filter(c => c.type === CourseType.Professional)
          break
        case 3:
          base = courses.filter(c => c.type === CourseType.Bootcamp)
          break
        default:
          base = courses
      }
      const filtered = base.filter(c => {
        const archivedMatch = filters.category === ARCHIVED_CATEGORY_VALUE ? c.archived === true : c.archived !== true
        const categoryMatch = filters.category && filters.category !== ARCHIVED_CATEGORY_VALUE ? c.category === filters.category : true
        return archivedMatch &&
          categoryMatch &&
          (filters.type ? c.type === filters.type : true) &&
          (filters.status ? c.status === filters.status : true)
      })
      setFilterCourses(sortCourses(filtered))
    } else {
      const filtered = courses.filter(c => {
        const archivedMatch = filters.category === ARCHIVED_CATEGORY_VALUE ? c.archived === true : c.archived !== true
        const categoryMatch = filters.category && filters.category !== ARCHIVED_CATEGORY_VALUE ? c.category === filters.category : true
        return c.name.toLowerCase().includes(q.toLowerCase()) &&
          archivedMatch &&
          categoryMatch &&
          (filters.type ? c.type === filters.type : true) &&
          (filters.status ? c.status === filters.status : true)
      })
      setFilterCourses(sortCourses(filtered))
    }
  }, [courses, filters, q, selected])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const getStatusColor = (status) => {
    switch (status) {
      case 'public':
        return 'var(--sf-green)'
      case 'draft':
        return 'var(--secondary)'
      case 'hidden':
        return 'var(--sf-red)'
      default:
        return 'var(--sf-grey)'
    }
  }

  const getCourseActions = (course) => {
    if (!course) return []
    const actions = [
      {
        label: "Duplica",
        icon: CopyIcon,
        onClick: () => {
          setAlert({
            open: true,
            title: `Duplicare ${course.name}?`,
            text: 'Scegli se vuoi duplicare solo la parte commerciale del corso (visibile in shop) oppure anche la parte didattica con edizioni e moduli.',
            actions: [
              {
                label: 'SOLO CORSO',
                onClick: async () => {
                  await duplicate(course.id, false)
                  setAlert((prev) => {
                    prev.open = false
                    return { ...prev }
                  })
                }
              },
              {
                label: 'TUTTO',
                onClick: async () => {
                  await duplicate(course.id, true)
                  setAlert((prev) => {
                    prev.open = false
                    return { ...prev }
                  })
                }
              }
            ]
          })
        }
      },
      {
        label: course.archived ? "Rimuovi dall'archivio" : "Archivia",
        icon: ArchiveIcon,
        onClick: () => {
          setAlert({
            open: true,
            title: course.archived ? `Rimuovere ${course.name} dall'archivio?` : `Archiviare ${course.name}?`,
            text: course.archived
              ? 'Il corso tornerà visibile nella lista principale.'
              : 'Il corso sarà spostato nella categoria Archiviati.',
            actions: [
              {
                label: course.archived ? 'RIPRISTINA' : 'ARCHIVIA',
                onClick: async () => {
                  await setCourseArchived(course.id, !course.archived)
                  setAlert((prev) => {
                    prev.open = false
                    return { ...prev }
                  })
                }
              }
            ]
          })
        }
      }
    ]
    if (course.status === 'draft') {
      actions.push(
        {
          label: "Elimina",
          icon: DeleteIcon,
          onClick: () => {
            setAlert({
              open: true,
              title: `Eliminare ${course.name}?`,
              text: 'L\'operazione non potrà essere annullata',
              actions: [
                {
                  label: 'ELIMINA',
                  onClick: async () => {
                    await deleteCourse(course.id)
                    setAlert((prev) => {
                      prev.open = false
                      return { ...prev }
                    })
                  },
                  color: 'var(--sf-red)'
                }
              ]
            })
          },
          color: 'var(--sf-red)'
        }
      )
    }
    return actions
  }

  useEffect(() => {
    if (!courses) return
    setFilterCourses(courses.filter(c => {
      const archivedMatch = filters.category === ARCHIVED_CATEGORY_VALUE ? c.archived === true : c.archived !== true
      const categoryMatch = filters.category && filters.category !== ARCHIVED_CATEGORY_VALUE ? c.category === filters.category : true
      return archivedMatch &&
        (filters.type ? c.type === filters.type : true) &&
        categoryMatch &&
        (filters.status ? c.status === filters.status : true) 
    }).sort((a, b) => { return a.name.localeCompare(b.name) }))
  },[filters, courses])

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>Didattica - Corsi</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <header className={styles.header}>
              <p className={styles.eyebrow}>Didattica</p>
              <h1>{t('courses.title')}</h1>
              <p>Consulta i corsi e accedi alle edizioni e ai moduli didattici.</p>
            </header>

            <Card appearance="modern" integratedTable title="Elenco corsi" actions={<span className={styles.resultCount} role="status">{loading ? 'Caricamento…' : `${filterCourses.length} corsi`}</span>}>
              <div className={styles.cardInner}>
                <div className={styles.search}>
                <TextInput
                  appearance="modern"
                  value={q}
                  onKeyUp={(value) => {
                    setQ(value);
                  }}
                  type="search"
                  placeholder={t("search")} />
                <button
                  type="button"
                  className={styles.filterButton}
                  aria-expanded={filtersOpen}
                  aria-controls="course-filters"
                  onClick={() => setFiltersOpen(open => !open)}>
                  <FilterIcon aria-hidden="true" />
                  Filtri
                </button>
                </div>
                <div id="course-filters" className={styles.filters} hidden={!filtersOpen}>
                  <div className={styles.filterField}>
                    <span>Tipo</span>
                  <Select appearance="modern" portal placeholder={"Tutti i tipi"} nullLabel="Tutti i tipi" nullable options={CourseTypes} selected={CourseTypes.find(o => o.value === filters.type)} onSelect={(item) => {
                    setFilters((prev) => {
                      prev.type = item?.value ?? null
                      return { ...prev }
                    })
                  }} />
                  </div>
                  <div className={styles.filterField}>
                    <span>Categoria</span>
                  <Select appearance="modern" portal placeholder={"Tutte le categorie"} nullLabel="Tutte le categorie" nullable options={categoryOptions} selected={categoryOptions.find(o => o.value === filters.category)} onSelect={(item) => {
                    setFilters((prev) => {
                      prev.category = item?.value ?? null
                      return { ...prev }
                    })
                  }} />
                  </div>
                  <div className={styles.filterField}>
                    <span>Stato</span>
                  <Select appearance="modern" portal placeholder={"Tutti gli stati"} nullLabel="Tutti gli stati" nullable options={CourseStatuses} selected={CourseStatuses.find(o => o.value === filters.status)} onSelect={(item) => {
                    setFilters((prev) => {
                      prev.status = item?.value ?? null
                      return { ...prev }
                    })
                  }} />
                  </div>
                </div>

                <div className={styles.courses}>
                  <Table
                    appearance="minimal"
                    style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }}
                    loading={loading}
                    loadingN={20}
                    columns={[
                      { key: 'id', label: 'ID', type: 'number'},
                      { key: 'thumbnail', label: 'COPERTINA', type: 'image' },
                      { key: 'name', label: 'NOME', type: 'string', wrap: true },
                      { key: 'type', label: 'TIPO', type: 'string' },
                      { key: 'status', label: 'STATO', type: 'custom' },
                      { key: 'actions', label: 'AZIONI', type: 'custom' }
                    ]}
                    elements={
                      filterCourses.map(c => ({
                        id: c.id,
                        name: c.name,
                        position: c.position,
                        description: c.description,
                        thumbnail: <img src={c.thumbnail} alt="" className={styles.thumbnail} />,
                        status: { value: c.status, type: 'string', component: <div className={styles.statusBadge}><StatusBadge appearance="soft" label={t(`status.${c.status}`)} color={getStatusColor(c.status)} /></div> },
                        type: t(`courses.types.${c.type}`),
                        actions: <MeatBallsMenu style={{ padding: '0.5rem' }} actions={getCourseActions(c)} />,
                        href: `/courses/${c.id}`
                      })).sort((a, b) => a.name.localeCompare(b.name))}
                    defaultSort={{ key: 'position', direction: 'asc' }}
                  />

                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <AlertDialog open={alert.open} title={alert.title} text={alert.text} actions={alert.actions} onClose={() => {
        setAlert((prev) => {
          prev.open = false
          return { ...prev }
        })
      }} />
    </HeaderFooterLayout>
  )

}

export default Courses
